import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Circle, CircleMarker, Polygon, Polyline, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useLanguage } from '../../contexts/LanguageContext';
import { SECTORS, WINDSOR_CENTER, type Coord } from '../../data/communications';
import { householdsNear } from '../../utils/households';
import type { AudiencePlace } from '../../types/composer';
const addressIcon = L.divIcon({
  className: '',
  html: `<div style="width:16px;height:16px;border-radius:9999px;background:#4F46E5;border:3px solid white;box-shadow:0 0 0 2px rgba(79,70,229,0.35);"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8]
});

/** Captures map clicks while the user traces a custom polygon. */
function DrawCapture({
  active,
  onAddPoint
}: {
  active: boolean;
  onAddPoint: (c: Coord) => void;
}) {
  useMapEvents({
    click(e) {
      if (active) onAddPoint([e.latlng.lat, e.latlng.lng]);
    }
  });
  return null;
}

/** Pans to the most recently added address or radius. */
function FocusOn({
  coord
}: {
  coord: Coord | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (coord) map.panTo(coord);
  }, [coord, map]);
  return null;
}
interface AudienceMapProps {
  places: AudiencePlace[];
  onToggleSector: (sectorId: string) => void;
  drawing: boolean;
  draftPoints: Coord[];
  onAddDraftPoint: (c: Coord) => void;
}

/** Every place of the audience drawn together, so the union reads at a glance. */
export function AudienceMap({
  places,
  onToggleSector,
  drawing,
  draftPoints,
  onAddDraftPoint
}: AudienceMapProps) {
  const {
    t,
    lang
  } = useLanguage();
  const selectedSectors = places.filter((p): p is Extract<AudiencePlace, {
    kind: 'sector';
  }> => p.kind === 'sector').map(p => p.sectorId);
  const lastPoint = useMemo(() => {
    const last = [...places].reverse().find(p => p.kind === 'address' || p.kind === 'radius');
    return last && (last.kind === 'address' || last.kind === 'radius') ? last.coord : null;
  }, [places]);
  return <div className="h-72 rounded-xl overflow-hidden border border-slate-200 relative" data-id="element-3772" data-mp="3i1gm8n5btkw4">
      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[400] pointer-events-none" data-id="element-3773" data-mp="3n1cghuo81xyh">
        <span className={`bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-sm border text-xs font-medium ${drawing ? 'border-indigo-200 text-[#4F46E5]' : 'border-slate-200 text-slate-500'}`} data-id="element-3774" data-mp="3r2o64073ij6o">
          {drawing ? t('Cliquez sur la carte pour tracer la zone', 'Click the map to trace the area') : t('Cliquez un secteur pour l’ajouter ou le retirer', 'Click a district to add or remove it')}
        </span>
      </div>

      <MapContainer center={WINDSOR_CENTER} zoom={13} scrollWheelZoom={false} style={{
      height: '100%',
      width: '100%'
    }} data-id="element-3775" data-mp="2fmptxngmfh18">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-3776" data-mp="281maxc2ancne" />
        <FocusOn coord={lastPoint} data-id="element-3777" data-mp="1z5elhk7xyzrj" />

        {SECTORS.map(s => {
        const selected = selectedSectors.includes(s.id);
        return <Polygon key={s.id} positions={s.polygon} eventHandlers={{
          click: () => {
            if (!drawing) onToggleSector(s.id);
          }
        }} pathOptions={{
          color: selected ? s.color : '#94A3B8',
          weight: selected ? 2 : 1,
          fillColor: selected ? s.color : '#CBD5E1',
          fillOpacity: selected ? 0.22 : 0.04,
          dashArray: selected ? undefined : '4 4',
          className: drawing ? '' : 'cursor-pointer'
        }} data-id="element-3778" data-mp="1p5e4i4xjovqi">
              <Tooltip direction="center" permanent={selected} data-id="element-3779" data-mp="31j46le5cenhw">
                {s.label[lang]}
              </Tooltip>
            </Polygon>;
      })}

        {places.map(place => {
        if (place.kind === 'address') {
          return <Marker key={place.id} position={place.coord} icon={addressIcon} data-id="element-3780" data-mp="2wqjb886sg2c9">
                <Tooltip direction="top" offset={[0, -8]} opacity={1} data-id="element-3781" data-mp="2irbb9d2swq70">
                  <span className="font-semibold" data-id="element-3782" data-mp="2iq09tjwtvuj2">{place.label}</span>
                </Tooltip>
              </Marker>;
        }
        if (place.kind === 'radius') {
          const households = householdsNear(place.coord, Number(place.radius));
          return <React.Fragment key={place.id} data-id="element-3783" data-mp="2dsqj80xpdctn">
                <Circle center={place.coord} radius={Number(place.radius)} pathOptions={{
              color: '#4F46E5',
              fillColor: '#4F46E5',
              fillOpacity: 0.1
            }} data-id="element-3784" data-mp="720lmai6n3n3" />
                <Marker position={place.coord} icon={addressIcon} data-id="element-3785" data-mp="1wi38dudoq48t" />
                {households.map(h => <CircleMarker key={h.id} center={h.coord} radius={3.5} pathOptions={{
              color: '#ffffff',
              weight: 1.5,
              fillColor: '#4F46E5',
              fillOpacity: 1
            }} data-id="element-3786" data-mp="1rkup1qgsvu5z">
                    <Tooltip direction="top" offset={[0, -6]} opacity={1} data-id="element-3787" data-mp="2aaj9uqqxtu7m">
                      <span className="font-semibold" data-id="element-3788" data-mp="2acncl5d3on6b">{h.address}</span>
                    </Tooltip>
                  </CircleMarker>)}
              </React.Fragment>;
        }
        if (place.kind === 'area') {
          return <Polygon key={place.id} positions={place.points} pathOptions={{
            color: '#4F46E5',
            fillColor: '#4F46E5',
            fillOpacity: 0.15
          }} data-id="element-3789" data-mp="uu2ef8cmyubl" />;
        }
        return null;
      })}

        {/* Area being traced */}
        {draftPoints.length >= 3 && <Polygon positions={draftPoints} pathOptions={{
        color: '#4F46E5',
        fillColor: '#4F46E5',
        fillOpacity: 0.1,
        dashArray: '4 4'
      }} data-id="element-3790" data-mp="1jlzx4o6srcuj" />}
        {draftPoints.length === 2 && <Polyline positions={draftPoints} pathOptions={{
        color: '#4F46E5'
      }} data-id="element-3791" data-mp="1yhz4fwm318ym" />}
        {draftPoints.map((p, i) => <Circle key={i} center={p} radius={12} pathOptions={{
        color: '#4F46E5',
        fillColor: '#fff',
        fillOpacity: 1
      }} data-id="element-3792" data-mp="22hq2mldxurd6" data-mpi={i} />)}
        <DrawCapture active={drawing} onAddPoint={onAddDraftPoint} data-id="element-3793" data-mp="2c7sswdtodr7t" />
      </MapContainer>
    </div>;
}